import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'
import { Check, Plus, X } from 'lucide-react'
import type { ServerApiClient } from '../../../../data/ServerApiClient'
import './useAddToReport.css'

// Cross-page "Add to report" / tag-to-report. Any CFO page can tag a chart,
// table, or value into the board pack via a shared provider. Persistence reuses
// the existing appendQuickBooksCfoPresentationBlock endpoint (GET+PUT of the
// presentation config) — no new backend needed — so this works against the live
// server today. The studio's curate panel then arranges/edits what was tagged.

export type AddToReportPayload = {
  title: string
  text?: string | null
  kind?: 'text' | 'kpi_grid' | 'statement_rollup' | 'statement_detail' | 'trend_chart' | 'waterfall' | 'ar_summary' | 'action_list' | 'ratio_card'
  section?: 'home' | 'profitAndLoss' | 'balanceSheet' | 'cashFlow' | 'accountsReceivable' | 'actions'
  source?: Record<string, any>
  content?: Record<string, any>
}

type AddToReportContextValue = {
  canAdd: boolean
  busy: boolean
  addToReport: (payload: AddToReportPayload) => Promise<void>
}

const AddToReportContext = createContext<AddToReportContextValue | null>(null)

type Toast = { id: number; message: string; tone: 'ok' | 'error' }

export function AddToReportProvider({
  appApi,
  workbookId,
  onViewReport,
  children,
}: {
  appApi: ServerApiClient
  workbookId: string | null
  /** Navigate to the Presentations studio (the toast "View" action). */
  onViewReport?: () => void
  children: ReactNode
}) {
  const [busy, setBusy] = useState(false)
  const [toast, setToast] = useState<Toast | null>(null)

  const showToast = useCallback((message: string, tone: 'ok' | 'error') => {
    const id = (typeof performance !== 'undefined' ? performance.now() : 0) + Math.floor(Math.random() * 1000)
    setToast({ id, message, tone })
    window.setTimeout(() => {
      setToast((cur) => (cur && cur.id === id ? null : cur))
    }, 4200)
  }, [])

  const addToReport = useCallback(
    async (payload: AddToReportPayload) => {
      if (!workbookId || busy) return
      setBusy(true)
      try {
        await appApi.appendQuickBooksCfoPresentationBlock(workbookId, {
          title: payload.title,
          text: payload.text ?? null,
          kind: payload.kind,
          section: payload.section,
          source: payload.source,
          content: payload.content,
        })
        showToast(`Added “${payload.title}” to the board pack.`, 'ok')
      } catch (err: any) {
        showToast(String(err?.message || err) || 'Could not add to the report.', 'error')
      } finally {
        setBusy(false)
      }
    },
    [appApi, busy, showToast, workbookId],
  )

  const value = useMemo<AddToReportContextValue>(
    () => ({ canAdd: Boolean(workbookId), busy, addToReport }),
    [addToReport, busy, workbookId],
  )

  return (
    <AddToReportContext.Provider value={value}>
      {children}
      {toast ? (
        <div className={`cfo-addreport-toast ${toast.tone}`} role="status" data-testid="cfo-add-to-report-toast">
          {toast.tone === 'ok' ? <Check size={15} /> : <X size={15} />}
          <span>{toast.message}</span>
          {toast.tone === 'ok' && onViewReport ? (
            <button type="button" onClick={() => { onViewReport(); setToast(null) }}>View</button>
          ) : null}
        </div>
      ) : null}
    </AddToReportContext.Provider>
  )
}

export function useAddToReport(): AddToReportContextValue | null {
  return useContext(AddToReportContext)
}

/**
 * A small "Add to report" affordance. Renders nothing when no provider is
 * mounted (so pages stay decoupled). `buildPayload` is called on click so the
 * payload can capture live state (the selected statement, the clicked chart
 * element, etc.).
 */
export function PinToReportButton({
  buildPayload,
  label = 'Add to report',
  compact = false,
  className,
}: {
  buildPayload: () => AddToReportPayload
  label?: string
  compact?: boolean
  className?: string
}) {
  const ctx = useAddToReport()
  if (!ctx || !ctx.canAdd) return null
  return (
    <button
      type="button"
      className={className || (compact ? 'cfo-addreport-btn compact' : 'cfo-addreport-btn')}
      disabled={ctx.busy}
      onClick={() => { void ctx.addToReport(buildPayload()) }}
      title="Add this to the board pack"
      data-testid="cfo-add-to-report"
    >
      <Plus size={compact ? 13 : 14} />
      {compact ? null : <span>{label}</span>}
    </button>
  )
}
